"use client"

import type React from "react"
import { useState, useEffect, useRef } from "react"
import { Box, Typography, Paper, IconButton, Tooltip } from "@mui/material"
import InfoIcon from "@mui/icons-material/Info"
import IconActive from "../../../../../asset/image/icon-park-active.svg"
import IconDisabled from "../../../../../asset/image/icon-park-disable.svg"
import "./AdvancedWANConfigurator.css"
import CommonModal from "../Modals/CommonModal"
import LinkBondingContent from "./LinkBondingContent"

interface WanLink {
  id: string;
  name: string;
  percentage?: number;
  wan_link_type?: string;
  wan_id?: number;
  alias?: string;
}


interface BondingRow {
  id: string
  links: WanLink[]
  mode: "Static" | "Dynamic"
}

interface DragState {
  isDragging: boolean
  draggedItem: any
  draggedFrom: string | null
  draggedIndex: number | null
}

interface AdvancedWANConfiguratorProps {
  bondingData: {
    id: number;
    interfaceName: string;
    priority: number;
    weight: number;
    wan_link_type?: string;
    wan_id?: number;
  }[];
  unusedLinks: WanLink[];
  onUnusedLinksChange: (links: WanLink[]) => void;
  onBondingChange?: (enabledWans: any[]) => void;
  isRenaming?: boolean;
  selectedRow?: any;
}


const AdvancedWANConfigurator: React.FC<AdvancedWANConfiguratorProps> = ({ bondingData, unusedLinks, onUnusedLinksChange, onBondingChange, isRenaming, selectedRow }) => {
  const newlyAddedWanIdsRef = useRef<Map<string, number>>(new Map());
  const [bondingRows, setBondingRows] = useState<BondingRow[]>([]);
  const [tempModalLinks, setTempModalLinks] = useState<WanLink[]>([]);

 const flattenBondingRowsToApiFormat = (rows: BondingRow[]) => {
  const allLinks: any[] = [];

  rows.forEach((row, index) => {
    if (row.links.length === 0) return; 

    const priority = (index + 10).toString();

    row.links.forEach((link) => {
      const matching = unusedLinks.find(
        u => u.name === link.name || u.alias === link.name || u.id === link.id
      );

      const resolvedWanId =
        link.wan_id ||
        (!isNaN(parseInt(link.id)) ? parseInt(link.id) : undefined) ||
        1;

      const resolvedWanLinkType = matching?.wan_link_type || link.wan_link_type || "";
      allLinks.push({
        interfaceName: link.name,
        weight: link.percentage?.toString() || "100",
        priority,
        wan_id: resolvedWanId,
        wan_link_type: resolvedWanLinkType,
      });
    });
  });

  return allLinks;
};



  const [isLinkBondingOpen, setLinkBondingOpen] = useState(false);
  const [dragState, setDragState] = useState<DragState>({
    isDragging: false,
    draggedItem: null,
    draggedFrom: null,
    draggedIndex: null,
  })

  const [editingRowIndex, setEditingRowIndex] = useState<number | null>(null);
  const hasRenderedOnce = useRef(false);

useEffect(() => {
  let newRows: BondingRow[] = [];

  if (bondingData && bondingData.length > 0) {
    const grouped = bondingData.reduce((acc, item) => {
      if (!acc[item.priority]) acc[item.priority] = [];

      acc[item.priority].push({
        id: String(item.wan_id),
        name: item.interfaceName,
        percentage: item.weight,
        wan_link_type: item.wan_link_type || "",
        wan_id: item.wan_id,
      });

      return acc;
    }, {} as Record<number, WanLink[]>);

    newRows = Object.entries(grouped).map(([priority, links]) => ({
      id: `row-${priority}`,
      links,
      mode: 'Static',
    }));
  }

  const cleared = [...newRows];
  const final = ensureTrailingEmptyRow(cleared);

  setBondingRows(final);  
}, [JSON.stringify(bondingData)]);  



  const dragRowRef = useRef<string | null>(null)
  const dragOverRowRef = useRef<string | null>(null)


  const redistributePercentages = (links: WanLink[]) => {
    const totalLinks = links.length
    if (totalLinks === 0) return

    const equalPercentage = Math.floor(100 / totalLinks)
    const remainder = 100 - equalPercentage * totalLinks

    links.forEach((link, i) => {
      link.percentage = equalPercentage + (i === 0 ? remainder : 0)
    })
  }

  const toggleBondingMode = (rowId: string) => {
    const newBondingRows = [...bondingRows]
    const rowIndex = newBondingRows.findIndex((row) => row.id === rowId)

    if (rowIndex !== -1) {
      newBondingRows[rowIndex].mode = newBondingRows[rowIndex].mode === "Static" ? "Dynamic" : "Static"

      if (newBondingRows[rowIndex].mode === "Static") {
        redistributePercentages(newBondingRows[rowIndex].links)
      } else {
        newBondingRows[rowIndex].links.forEach((link) => {
          delete link.percentage
        })
      }

      setBondingRows(newBondingRows)


    }
  }


  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, item: any, sourceId: string, index: number) => {
    e.dataTransfer.setData("text/plain", JSON.stringify({ item, sourceId, index }))
    setDragState({
      isDragging: true,
      draggedItem: item,
      draggedFrom: sourceId,
      draggedIndex: index,
    })
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
  }
  const handleDrop = (e: React.DragEvent<HTMLDivElement>, targetId: string, targetIndex = -1) => {
    e.preventDefault();

    try {
      const data = JSON.parse(e.dataTransfer.getData("text/plain"));
      const { item, sourceId, index: sourceIndex } = data;

      if (sourceId === targetId && sourceIndex === targetIndex) return;

      if (sourceId === "unused-links" && targetId.startsWith("row-")) {
        const rawLink = unusedLinks[sourceIndex];
        const isStatic = bondingRows.find((row) => row.id === targetId)?.mode === "Static";

        const enrichedLink: WanLink = {
          id: rawLink.id,
          name: rawLink.alias || rawLink.name,
          wan_id: parseInt(rawLink.id),
          wan_link_type: rawLink.wan_link_type || "",
          ...(isStatic ? { percentage: 0 } : {})
        };


        const key = `${enrichedLink.name}_${targetId}`;
        newlyAddedWanIdsRef.current.set(key, enrichedLink.wan_id!);


        const newUnused = [...unusedLinks];
        newUnused.splice(sourceIndex, 1);
        onUnusedLinksChange(newUnused);

        const updatedRows = [...bondingRows];
        const rowIndex = updatedRows.findIndex((r) => r.id === targetId);
        if (rowIndex !== -1) {
          if (targetIndex === -1) {
            updatedRows[rowIndex].links.push(enrichedLink);
          } else {
            updatedRows[rowIndex].links.splice(targetIndex, 0, enrichedLink);
          }

          if (updatedRows[rowIndex].mode === "Static") {
            redistributePercentages(updatedRows[rowIndex].links);
          }

          const final = ensureTrailingEmptyRow(updatedRows);
          setBondingRows(final);
          onBondingChange?.(flattenBondingRowsToApiFormat(final));
        }
      }


      else if (sourceId.startsWith("row-") && targetId.startsWith("row-")) {
        const newBondingRows = [...bondingRows];
        const sourceRowIndex = newBondingRows.findIndex((row) => row.id === sourceId);
        const targetRowIndex = newBondingRows.findIndex((row) => row.id === targetId);

        if (sourceRowIndex !== -1 && targetRowIndex !== -1) {
          const [movedLink] = newBondingRows[sourceRowIndex].links.splice(sourceIndex, 1);
          const clonedLink = { ...movedLink };

          if (!clonedLink.wan_link_type) {
            clonedLink.wan_link_type = movedLink?.wan_link_type || "";
          }

          if (targetIndex === -1) {
            newBondingRows[targetRowIndex].links.push(clonedLink);
          } else {
            newBondingRows[targetRowIndex].links.splice(targetIndex, 0, clonedLink);
          }

          if (newBondingRows[sourceRowIndex].mode === "Static") {
            redistributePercentages(newBondingRows[sourceRowIndex].links);
          }
          if (newBondingRows[targetRowIndex].mode === "Static") {
            redistributePercentages(newBondingRows[targetRowIndex].links);
          }

          const final = ensureTrailingEmptyRow(newBondingRows);
          setBondingRows(final);
          onBondingChange?.(flattenBondingRowsToApiFormat(final));
        }
      }
      else if (sourceId.startsWith("row-") && targetId === "unused-links") {
        const newBondingRows = [...bondingRows];
        const rowIndex = newBondingRows.findIndex((row) => row.id === sourceId);
        if (rowIndex !== -1) {
          const [movedLink] = newBondingRows[rowIndex].links.splice(sourceIndex, 1);
          const { percentage, ...cleanedLink } = movedLink;

          cleanedLink.wan_id = movedLink.wan_id;
          cleanedLink.wan_link_type = movedLink.wan_link_type;

          const updatedUnused = [...unusedLinks];
          if (targetIndex === -1) {
            updatedUnused.push(cleanedLink);
          } else {
            updatedUnused.splice(targetIndex, 0, cleanedLink);
          }

          onUnusedLinksChange(updatedUnused);

          if (newBondingRows[rowIndex].mode === "Static") {
            redistributePercentages(newBondingRows[rowIndex].links);
          }
          const final = ensureTrailingEmptyRow(newBondingRows);
          setBondingRows(final);
          onBondingChange?.(flattenBondingRowsToApiFormat(final));
        }
      }
    } catch (error) {
      console.error("Error parsing drag data:", error);
    }

    setDragState({
      isDragging: false,
      draggedItem: null,
      draggedFrom: null,
      draggedIndex: null,
    });
  };


  const handleRowDragStart = (e: React.DragEvent<HTMLDivElement>, rowId: string) => {
    e.dataTransfer.setData("row", rowId)
    dragRowRef.current = rowId
  }

  const handleRowDragOver = (e: React.DragEvent<HTMLDivElement>, rowId: string) => {
    e.preventDefault()
    dragOverRowRef.current = rowId
  }

  const handleRowDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();

    if (dragState.isDragging) return;
    const draggedRowId = dragRowRef.current;
    const targetRowId = dragOverRowRef.current;
    if (draggedRowId && targetRowId && draggedRowId !== targetRowId) {
      const newBondingRows = [...bondingRows];
      const draggedRowIndex = newBondingRows.findIndex((row) => row.id === draggedRowId);
      const targetRowIndex = newBondingRows.findIndex((row) => row.id === targetRowId);

      if (draggedRowIndex !== -1 && targetRowIndex !== -1) {
        const [draggedRow] = newBondingRows.splice(draggedRowIndex, 1);
        newBondingRows.splice(targetRowIndex, 0, draggedRow);
        const updatedRows = ensureTrailingEmptyRow(newBondingRows);
        setBondingRows(updatedRows);


        onBondingChange?.(flattenBondingRowsToApiFormat(updatedRows));  

      }
    }

    dragRowRef.current = null;
    dragOverRowRef.current = null;
  };


  const ensureTrailingEmptyRow = (rows: BondingRow[]): BondingRow[] => {
    const cleanedRows = rows.filter((row, idx) => row.links.length > 0 || idx === rows.length - 1);
    const lastRow = cleanedRows[cleanedRows.length - 1];
    const lastRowEmpty = lastRow?.links?.length === 0;

    if (!lastRowEmpty) {
      const existingIds = cleanedRows.map(row => parseInt(row.id.replace("row-", ""))).filter(n => !isNaN(n));
      const nextId = Math.max(...existingIds, 0) + 1;

      cleanedRows.push({
        id: `row-${nextId}`,
        links: [],
        mode: "Static",
      });
    }

    return cleanedRows;
  };





  const total = tempModalLinks.reduce((sum, link) => sum + (link.percentage || 0), 0);

  const handleSaveLinkBonding = () => {
    if (editingRowIndex !== null && total <= 100) {
      const updatedRows = [...bondingRows];
      updatedRows[editingRowIndex].links = tempModalLinks;
      const finalRows = ensureTrailingEmptyRow(updatedRows);
      setBondingRows(finalRows);

      onBondingChange?.(flattenBondingRowsToApiFormat(updatedRows)); 


      setLinkBondingOpen(false);
    }
  };

  const colorStops = [
    { max: 10, color: "rgba(130, 186, 255, 1)" },
    { max: 20, color: "rgba(120, 174, 241, 1)" },
    { max: 30, color: "rgba(109, 162, 228, 1)" },
    { max: 40, color: "rgba(99, 149, 214, 1)" },
    { max: 50, color: "rgba(88, 137, 200, 1)" },
    { max: 60, color: "rgba(78, 125, 187, 1)" },
    { max: 70, color: "rgba(67, 113, 173, 1)" },
    { max: 80, color: "rgba(57, 100, 159, 1)" },
    { max: 90, color: "rgba(46, 88, 146, 1)" },
  ];

  return (<>
    <Box sx={{ margin: "0 auto", paddingTop: 1.5, mb: 1 }}>


      <Typography
        variant="body2"
        sx={{
          fontFamily: "Inter",
          fontWeight: 500,
          fontSize: "12px",
          lineHeight: "10px",
          letterSpacing: "0%",
          mr: 1,
          mb: 1,
          color: "rgba(0, 0, 0, 0.6)",
        }}
      >Unused WAN Links</Typography>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,

          p: 1,
          mb: 1.5,
          border: "1px dashed #e0e0e0",
          borderRadius: "4px",
        }}
        onDragOver={handleDragOver}
        onDrop={(e) => handleDrop(e, "unused-links")}
      >
        {unusedLinks.map((link, index) => (
          <Box
            key={link.id}
            draggable
            onDragStart={(e) => handleDragStart(e, link, "unused-links", index)}
            sx={{
              minWidth: "20px",
              height: "10px",
              padding: "8px",
              borderRadius: "6px",
              backgroundColor: "#EBEBEB",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontFamily: "Inter",
              fontWeight: 500,
              fontSize: "14px",
              lineHeight: "10px",
              letterSpacing: "0%",
              color: "#6E7079",
              cursor: "grab",
            }}
          >
            {link.name}
          </Box>

        ))}
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography
          variant="body2"
          sx={{
            fontFamily: "Inter",
            fontWeight: 500,
            fontSize: "12px",
            lineHeight: "10px",
            letterSpacing: "0%",
            mr: 1,
            mb: 1,
            color: "rgba(0, 0, 0, 0.6)",
          }}
        >
          WAN Priority & Bonding
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", mb: 0.1, mt: { xs: 1, sm: 0 } }}>
          <Typography
            variant="body2"
            sx={{
              fontFamily: "Inter",
              fontWeight: 500,
              fontSize: "12px",
              lineHeight: "10px",
              letterSpacing: "0%",
              mr: 1,
              color: "rgba(0, 0, 0, 0.6)",
            }}
          >
            Link Bonding
          </Typography>
          <Tooltip title={<span style={{ fontSize: "12px" }}>WAN Link Bonding can be set to Static. In Static setting, a configured % of traffic is set for each WAN link.</span>}>
            <InfoIcon color="primary" fontSize="small" />
          </Tooltip>
        </Box>
      </Box>

      <Box
        className="bonding-scroll-container"
        onDragOver={(e) => e.preventDefault()}
        onDrop={handleRowDrop}

        sx={{
          overflowY: "auto",
          pr: 1, 
        }}
      >
        {bondingRows.map((row, rowIndex) => (
          <Paper
            key={row.id}
            sx={{
              display: "flex",

              padding: "4px",
              marginBottom: "8px",
              alignItems: "center",
              borderRadius: "4px",
              boxShadow: "none !important",
              border: "1px solid #e0e0e0",
              cursor: "grab",
              minHeight: "20px",
            }}
            draggable
            onDragStart={(e) => handleRowDragStart(e, row.id)}
            onDragOver={(e) => handleRowDragOver(e, row.id)}
          >
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(2, 4px)",
                rowGap: "2px",
                columnGap: "4px",
                marginRight: "12px",
                marginLeft: "5px",
              }}
            >
              {[...Array(6)].map((_, i) => (
                <Box
                  key={i}
                  sx={{
                    width: "4px",
                    height: "4px",
                    backgroundColor: "#EBEBEB",
                    borderRadius: "50%",
                  }}
                />
              ))}
            </Box>

            <Box
              sx={{
                display: "flex",
                flexGrow: 1,
                flexWrap: "wrap",
                gap: 1,
                minWidth: 0,
                overflowWrap: "anywhere",
                minHeight: "20px",
                alignItems: "center",
                padding: "4px 0px",
              }}
              onDragOver={handleDragOver}
              onDrop={(e) => 
                selectedRow['tierAlias'] === 'Essential' && row.links.length >= 1
                  ? undefined
                  : handleDrop(e, row.id)
              }
            >
              {row.links.length === 0 ? (
                <Typography
                  sx={{
                    fontFamily: "Inter",
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#8A8A8A",
                    pl: 1,
                  }}
                >
                  {`Priority ${rowIndex + 1}`}
                </Typography>
              ) : (
                row.links.map((link, linkIndex) => {
                  const getPercentColor = (value: number) => {
                    for (const stop of colorStops) {
                      if (value <= stop.max) return stop.color;
                    }
                    return "rgba(38, 76, 134, 1)";
                  };


                  return (
                    <Box
                      key={link.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, link, row.id, linkIndex)}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        cursor: "grab",
                        borderRadius: "6px",
                        padding: "0px",
                        height: "auto",
                        width: "fit-content",
                        maxWidth: "100%",
                        backgroundColor: row.mode === "Static" ? "transparent" : "inherit",
                      }}
                    >
                      {row.mode === "Static" && link.percentage !== undefined && (
                        <Box
                          sx={{
                            width: "32px",
                            height: "12px",
                            backgroundColor: getPercentColor(link.percentage),
                            borderRadius: "6px 0 0 6px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontFamily: "Inter",
                            fontSize: "14px",
                            fontWeight: 500,
                            lineHeight: "10px",
                            color: "#fff",
                            padding: "8px",
                          }}
                        >
                          {link.percentage}
                        </Box>
                      )}

                      <Box
                        sx={{
                          backgroundColor: "#244C84",
                          height: "12px",
                          borderRadius: row.mode === "Static" ? "0 6px 6px 0" : "6px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          fontFamily: "Inter",
                          fontSize: "14px",
                          fontWeight: 500,
                          lineHeight: "10px",
                          color: "#fff",
                          padding: "8px",
                          minWidth: "78px",
                        }}
                      >
                        {link.name}
                      </Box>
                    </Box>
                  );
                })
              )}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", ml: 2 }}>
              {/* <label className="custom-toggle">
        <input
          type="checkbox"
          checked={row.mode === "Static"}
          onChange={() => toggleBondingMode(row.id)}
        />
        <span className="slider">{row.mode}</span>
      </label> */}

              <Typography
                sx={{
                  fontFamily: "Inter",
                  fontWeight: 400,
                  fontSize: "12px",
                  lineHeight: "10px",
                  color: "#6E7079",
                  mr: 0.5,
                }}
              >
                Static
              </Typography>

              <IconButton
               onClick={() => {
                if (row.mode === "Static" && row.links.length > 1) {
                  setEditingRowIndex(rowIndex);
                  setTempModalLinks(
                    JSON.parse(JSON.stringify(bondingRows[rowIndex].links)).map(link => ({
                      ...link,
                      percentage: Number(link.percentage) || 0,
                    }))
                  );
                  
                  setLinkBondingOpen(true);
                }
              }}
              
                disabled={row.links.length <= 1}
                sx={{
                  opacity: row.links.length <= 1 ? 0.5 : 1,
                  cursor: row.links.length <= 1 ? "not-allowed" : "pointer",
                }}
              >
                <img
                  src={row.mode === "Static" ? IconActive : IconDisabled}
                  alt="mode icon"
                  style={{ width: 20, height: 20 }}
                />
              </IconButton>

            </Box>
          </Paper>
        ))}

      </Box>
    </Box>

    <CommonModal
      open={isLinkBondingOpen}
      onClose={() => setLinkBondingOpen(false)}
      onSave={handleSaveLinkBonding}

      title="Link Bonding"
      maxWidth="436px"
      disableSave={total > 100 || total < 100}
      showOnlineOffline={false}
      showSyncStatus={false}
    >
      <LinkBondingContent
        links={tempModalLinks}
        onChange={setTempModalLinks}
      />

    </CommonModal>


  </>
  )
}

export default AdvancedWANConfigurator;